import type { Car } from '@/api/cars/types';

export enum CarAlertType {
  CAR_DISCONNECTED = 'car_disconnected',
  DIGITAL_KEY_DISCONNECTED = 'digital_key_disconnected',
  KEY_LIMIT = 'key_limit',
}

// A Tesla can hold at most 19 keys; warn the owner once they're near the cap.
export const MAX_KEY_COUNT = 19;
export const KEY_LIMIT_WARN_THRESHOLD = 14;

// Alerts are only surfaced for trips that haven't started yet.
const ALERT_STATUSES = ['open', 'verified', 'confirmed'];

export const shouldShowCarAlerts = (status?: string): boolean =>
  !!status && ALERT_STATUSES.includes(status);

/**
 * Returns the applicable car connection alerts, highest priority first:
 * car disconnected > digital key disconnected > near key limit.
 * Resla-managed and offline cars are never flagged.
 */
export const getCarAlerts = (car?: Car): CarAlertType[] => {
  if (!car || car.isReslaCar || car.isOffline) return [];

  if (!car.isStandardFleetConnected) {
    return [CarAlertType.CAR_DISCONNECTED];
  }

  const alerts: CarAlertType[] = [];

  if (!car.standardFleetVirtualKeyConnected) {
    alerts.push(CarAlertType.DIGITAL_KEY_DISCONNECTED);
  }

  if (car.keyCount != null && car.keyCount > KEY_LIMIT_WARN_THRESHOLD) {
    alerts.push(CarAlertType.KEY_LIMIT);
  }

  return alerts;
};

export const getReservationCarAlerts = (
  status?: string,
  car?: Car
): CarAlertType[] => {
  if (!shouldShowCarAlerts(status)) return [];
  return getCarAlerts(car);
};

export const getPrimaryReservationCarAlert = (
  status?: string,
  car?: Car
): CarAlertType | null => getReservationCarAlerts(status, car)[0] ?? null;

// Short one-line copy for the dashboard trip cards.
export const CAR_ALERT_SHORT_LABEL: Record<CarAlertType, string> = {
  [CarAlertType.CAR_DISCONNECTED]: 'Car disconnected. Reconnect to continue.',
  [CarAlertType.DIGITAL_KEY_DISCONNECTED]:
    'Digital key disconnected. Reconnect to continue.',
  [CarAlertType.KEY_LIMIT]: "You're near the key limit. Remove unused keys.",
};

// Full alert content for the reservation detail screen.
export const CAR_ALERT_CONTENT: Record<
  CarAlertType,
  { title: string; body: string; buttonText: string }
> = {
  [CarAlertType.CAR_DISCONNECTED]: {
    title: 'Car disconnected',
    body: "We've lost connection to your Tesla, so your keys and trip details can't sync. Reconnect to get everything working again.",
    buttonText: 'Reconnect Car',
  },
  [CarAlertType.DIGITAL_KEY_DISCONNECTED]: {
    title: 'Digital key disconnected',
    body: "We've lost connection to your digital key. Reconnect it so it's ready for pickup.",
    buttonText: 'Reconnect Digital Key',
  },
  [CarAlertType.KEY_LIMIT]: {
    title: 'You are almost at the key limit',
    body: `This car can only hold ${MAX_KEY_COUNT} keys, and you're close. Remove a few you don't use so your next key adds cleanly. Leave keys marked Eon connected, those keep your trip working.`,
    buttonText: 'How To Manage Keys?',
  },
};
